import ApiService, { HttpMethod } from "../../../commons/api/api_service";
import BaseResponse from "../../../commons/api/base_response";
import { Endpoints } from "../../../commons/api/endpoints";
import { ProviderVM } from "../../../commons/base/providers/view_model";
import {
    ShowErrorViewModel,
    ShowSnackbarModel,
} from "../../../commons/scaffold/scaffold";
import OutletModel from "../../models/outlet_model";
import SelectedOutletModel from "../../models/selected_outlet_model";
import { OutletType } from "../../models/outlet_type_enum";
import ScheduleModel from "../../models/schedule_model";
import Swal from "sweetalert2";

interface UpdateFormOpsRequestBody {
    title: string;
    content: string;
    outletType: OutletType;
    schedules: ScheduleModel[];
    outlets: SelectedOutletModel[];
}

interface FormOpsReminderDetailResponse {
    id: number;
    title: string;
    content: string;
    outletType: OutletType;
    schedules: ScheduleModel[];
    outlets: SelectedOutletModel[];
}

export default class FormOpsReminderUpdateViewModel extends ProviderVM {
    // ui states
    isLoading: boolean = false;
    isSubmitting: boolean = false;
    errorModel?: ShowErrorViewModel;
    snackbarModel?: ShowSnackbarModel;

    // fetched data
    outlets: OutletModel[] = [];

    // form data
    formId: number = 0;
    title: string = "";
    content: string = "";
    outletType: OutletType = OutletType.SHACK;
    scheduleMap: Map<number, ScheduleModel> = new Map<number, ScheduleModel>();
    selectedOutletsMap: Map<number, OutletModel> = new Map<
        number,
        OutletModel
    >();

    get outletsInOptions() {
        const selectedOutletIds = [...this.selectedOutletsMap.keys()];
        return this.outlets.filter(
            (outlet) => !selectedOutletIds.includes(outlet.id)
        );
    }

    constructor(args: { formId: number }) {
        super();
        this.fetchPreloading();
        this.fetchFormDetail(args.formId);
    }

    fetchPreloading() {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(Endpoints.formOpsReminderPreloading, {
            httpMethod: HttpMethod.get,
            onSuccess: (
                response: BaseResponse<{
                    outlets: OutletModel[];
                }>
            ) => {
                if (response.code === 200) {
                    const outletsList = response.data?.outlets ?? [];

                    this.emit(() => {
                        this.isLoading = false;
                        this.outlets = outletsList;
                    });
                } else {
                    this.emit(() => {
                        this.isLoading = false;
                        this.errorModel = {
                            action: () => {},
                            message: response.message,
                        };
                    });
                }
            },
            onFailure: (error) => {
                this.emit(() => {
                    this.isLoading = false;
                    this.errorModel = {
                        action: () => {},
                        message: `${error}`,
                    };
                });
            },
        });
    }

    fetchFormDetail(formId: number) {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(
            `${Endpoints.formOpsReminderGetDetail}/${formId}` as Endpoints,
            {
                httpMethod: HttpMethod.get,
                onSuccess: (
                    response: BaseResponse<FormOpsReminderDetailResponse>
                ) => {
                    if (response.code === 200) {
                        const schedulesResponse =
                            response.data?.schedules ?? [];
                        const selectedOutletsResponse =
                            response.data?.outlets ?? [];

                        const scheduleMap: Map<number, ScheduleModel> = new Map<
                            number,
                            ScheduleModel
                        >();

                        schedulesResponse.forEach((schedule) => {
                            scheduleMap.set(schedule.day, schedule);
                        });

                        const selectedOutletsMap: Map<number, OutletModel> =
                            new Map<number, OutletModel>();

                        selectedOutletsResponse
                            .filter((outlet) => outlet.isActive)
                            .forEach((outlet) => {
                                const outletName =
                                    this.outlets.filter(
                                        (option) =>
                                            option.id === outlet.outletId
                                    )[0]?.name ?? "";

                                selectedOutletsMap.set(outlet.outletId, {
                                    id: outlet.outletId,
                                    name: outletName,
                                });
                            });

                        this.emit(() => {
                            this.formId = response.data?.id ?? 0;
                            this.title = response.data?.title ?? "";
                            this.content = response.data?.content ?? "";
                            this.outletType =
                                response.data?.outletType ?? OutletType.CUSTOM;
                            this.scheduleMap = scheduleMap;
                            this.selectedOutletsMap = selectedOutletsMap;
                        });
                    } else {
                        this.emit(() => {
                            this.isLoading = false;
                            this.errorModel = {
                                action: () => {},
                                message: response.message,
                            };
                        });
                    }
                },
                onFailure: (error) => {
                    this.emit(() => {
                        this.isLoading = false;
                        this.errorModel = {
                            action: () => {},
                            message: `${error}`,
                        };
                    });
                },
            }
        );
    }

    updateTitle(value: string) {
        this.emit(() => {
            this.title = value;
        });
    }

    updateContent(value: string) {
        this.emit(() => {
            this.content = value;
        });
    }

    updateOutletType(value: OutletType) {
        this.emit(() => {
            this.outletType = value;
        });
    }

    updateSchedule(args: { day: number; time?: string; isActive?: boolean }) {
        const oldValue = this.scheduleMap.get(args.day);

        if (!oldValue) return;

        const newValue: ScheduleModel = {
            ...oldValue,
            time: args.time ?? oldValue.time,
            isActive: args.isActive ?? oldValue.isActive,
        };

        const updatedMap = new Map(this.scheduleMap);
        updatedMap.set(newValue.day, newValue);

        this.emit(() => {
            this.scheduleMap = updatedMap;
        });
    }

    toggleOutlet(outletModel: OutletModel) {
        const updatedMap = new Map(this.selectedOutletsMap);

        if (updatedMap.has(outletModel.id)) {
            updatedMap.delete(outletModel.id);
        } else {
            updatedMap.set(outletModel.id, outletModel);
        }

        this.emit(() => {
            this.selectedOutletsMap = updatedMap;
        });
    }

    submitForm(args: { onSuccess: () => void }) {
        Swal.fire({
            title: "Konfirmasi",
            text: "Apaka sudah yakin?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Gass",
        }).then((result) => {
            if (result.isConfirmed) {
                this.emit(() => {
                    this.isSubmitting = true;
                });

                ApiService.fetch<UpdateFormOpsRequestBody, BaseResponse<{}>>(
                    `${Endpoints.formOpsReminderUpdate}/${this.formId}` as Endpoints,
                    {
                        httpMethod: HttpMethod.put,
                        parameters: {
                            title: this.title,
                            content: this.content,
                            outletType: this.outletType,
                            schedules: [...this.scheduleMap.values()].map(
                                (schedule) => ({
                                    day: schedule.day,
                                    time: schedule.time,
                                    isActive: schedule.isActive,
                                })
                            ),
                            outlets: [...this.selectedOutletsMap.values()].map(
                                (outlet) => ({
                                    outletId: outlet.id,
                                    isActive: true,
                                })
                            ),
                        },
                        onSuccess: (response) => {
                            if (response.code === 200) {
                                this.emit(() => {
                                    this.isSubmitting = false;
                                    this.snackbarModel = {
                                        key: Date.now().toString(),
                                        message: response.message,
                                        type: "normal",
                                    };
                                });

                                setTimeout(() => args.onSuccess(), 500);
                            } else {
                                this.emit(() => {
                                    this.isSubmitting = false;
                                    this.errorModel = {
                                        action: () => {},
                                        message: response.message,
                                    };
                                });
                            }
                        },
                        onFailure: (error) => {
                            this.emit(() => {
                                this.isSubmitting = false;
                                this.errorModel = {
                                    action: () => {},
                                    message: `${error}`,
                                };
                            });
                        },
                    }
                );
            }
        });
    }
}
